/**
 * HarmonyManager.js
 * Global Harmonic Validation and Resolution.
 * Enhances progression generation with Functional Harmonic Intelligence.
 */

import { Random } from './Random.js';
import { FUNCTIONAL_SUBSTITUTIONS, NOTES, SCALE_INTERVALS, normalizeKey } from '../data/theoryConstants.js';
import { HARMONY_WEIGHTS } from '../data/StrategyConfig.js';
import { HARMONIC_GRAPH, getGraphChordQuality } from '../data/FunctionalHarmonyGraph.js';
import { detectCadence } from './Analysis/CadenceDetector.js';

// Configuration: Intervals to penalize
const CLASH_INTERVALS = new Set([1, 6]); // Minor 2nd (1) and Tritone (6)

export class HarmonyManager {
    constructor(random) {
        this.random = random || new Random();
    }

    /**
     * Generates a chord progression using a Weighted Directed Graph (Functional Harmony).
     * Replaces the static template system with a dynamic walker.
     */
    generateGraphProgression(structure, context) {
        if (!structure) return [];

        const rootKey = context.rootKey || 'C';
        const scaleType = context.scaleType || 'major';
        // Normalize global root if needed, though we use section keys usually
        const rootIdx = NOTES.indexOf(normalizeKey(rootKey));

        const progression = [];
        let currentBar = 0;
        let currentDegree = 0; // Start on Tonic (I)

        structure.forEach((section, sectIdx) => {
            const bars = section.bars || 4;
            const timeSig = section.timeSig || 4;
            const sectionKey = section.key || rootKey;
            const sectionScale = section.scale || scaleType;

            const sectionRootIdx = NOTES.indexOf(normalizeKey(sectionKey));
            const sectionIntervals = SCALE_INTERVALS[sectionScale] || SCALE_INTERVALS.major;

            // Select appropriate graph
            const graphType = (sectionScale === 'minor' || sectionScale === 'harmonic_minor') ? 'minor' : 'major';
            const sectionGraph = HARMONIC_GRAPH[graphType];

            // Reset logic: Start fresh on I for major sections unless it's a Bridge
            // This ensures sections feel grounded.
            if (section.type === 'INTRO' || section.type === 'VERSE' || section.type === 'CHORUS') {
                currentDegree = 0;
            }

            // --- Dynamic Harmonic Rhythm Logic (Phase 2) ---
            // Allow vectors to influence pacing.
            let subBars = 1;

            // VARIATION: Seeded Harmonic Pacing
            // Randomly choose between Fast (1 chord/bar) and Slow (1 chord/2 bars) if density is moderate
            // This prevents every song from having the same harmonic rhythm.
            const pacingSeed = (context.config && context.config.seed) ? context.config.seed : 0;
            const pacingRoll = Math.abs(Math.sin(pacingSeed + sectIdx)) * 10; // 0-10

            if (context.config && context.config.vectors) {
                const density = context.config.vectors.melodicDensity;
                // Formula: 1 / density (Inverse Relationship)
                subBars = Math.min(8, Math.round(1 / Math.max(0.1, density)));

                // Apply Pacing Variance (Moderate density only)
                if (density > 0.3 && density < 0.7) {
                    if (pacingRoll > 7) subBars *= 2; // Slower
                    else if (pacingRoll < 2) subBars = Math.max(1, Math.floor(subBars / 2)); // Faster
                }
            }

            // Override for specific genres if needed (e.g. Minimalist)
            if (context.traits && (context.traits.name === 'Minimalist' || context.traits.name.includes('Drone') || context.traits.name === 'Gothic' || context.traits.name === 'Sci-Fi')) {
                subBars = Math.max(subBars, 4);
            }

            // Fix: Clamp for Cinematic to prevent score penalty (Target 2.0, max 3.0)
            // Also ensure MINIMUM duration (Slow Burn)
            if (context.traits && (context.traits.name.includes('Epic') || context.traits.name.includes('Cinematic'))) {
                subBars = Math.max(2, Math.min(3, subBars));
            }

            // Fix: Force Fast Harmonic Rhythm for Baroque (Target 1.0)
            if (context.traits && context.traits.name === 'Baroque') {
                subBars = 1; // 1 Chord per Bar (or faster if density is high, but 1.0 is target)
            }

            // --- Fast Harmonic Rhythm Logic ---
            let subDivisions = 1;

            // VARIATION: Hyper-Fast Injection
            // Small chance (10%) to force 2 chords/bar for high intensity sections even if not Action
            const intensity = (context.config && context.config.vectors) ? context.config.vectors.energy : 0.5;
            if (intensity > 0.8 && pacingRoll > 8) {
                subDivisions = 2;
                subBars = 1;
            }

            // Fix: Force Action/Chase to 2 chords/bar (0.5 bars/chord)
            // Also detect from traits.harmonicRhythm string if available
            if (context.traits && (context.traits.name.includes('Action') || context.traits.name.includes('Chase'))) {
                subDivisions = 2; // 2 Chords per bar (Action Archetype)
                subBars = 1; // Reset slow rhythm
            }
            // Generalize: If trait explicitly requests fast/hyper
            else if (context.traits && context.traits.harmonicRhythm === 'fast') {
                subDivisions = 2;
                subBars = 1;
            }
            else if (context.traits && context.traits.harmonicRhythm === 'hyper') {
                subDivisions = 4;
                subBars = 1;
            }

            for (let b = 0; b < bars; b++) {
                // Check if we should hold previous chord (Harmonic Rhythm Pacing)
                // If subBars > 1, only generate on boundaries
                if (subBars > 1 && b % subBars !== 0 && progression.length > 0) {
                     // Extend previous chord duration
                     const lastChord = progression[progression.length - 1];
                     // Only extend if it belongs to the same section (it does, inside this loop)
                     lastChord.duration += 4; // Add 1 bar (4 beats)
                     continue; // Skip generation
                }

                const beatDuration = timeSig / subDivisions; // Dynamic Time Signature Support

                for (let s = 0; s < subDivisions; s++) {
                    // 3. Construct Chord
                    // Sample tension at the exact moment
                    const tension = context.getTension ? context.getTension(currentBar + b + (s/subDivisions)) : 0.5;
                    const isHorror = context.traits && context.traits.name === 'Horror / Tension';

                    let chord;

                    if (isHorror) {
                        // PHASE 5: MOCK CHORD ADAPTER (Horror Clusters)
                        // Generate highly dissonant cluster "chord"
                        // Scale: 'chromatic' acts as safety pin against quantization
                        const rootPC = Math.floor(this.random.next() * 12); // Random root (Atonal)

                        // Connect Vectors to Dissonance
                        // Use harmonicComplexity as a continuous probability weight
                        const complexity = (context.config && context.config.vectors) ? context.config.vectors.harmonicComplexity : 0.5;

                        let clusterIntervals = [0, 6]; // Base: Root + Tritone (Always)

                        // Probabilistic Addition (Continuous Gradient)
                        // Min 2nd (Crunch) - Easy to add (Force if low complexity to ensure baseline horror)
                        // Boosted to 0.8 base for calibration
                        if (this.random.bool(Math.max(0.8, complexity + 0.4))) {
                             clusterIntervals.push(1);
                        }
                        // Maj 7th (Sharp) - Medium
                        if (this.random.bool(Math.min(1.0, complexity + 0.3))) {
                             clusterIntervals.push(11);
                        }
                        // Maj 2nd (Cluster) - High
                        if (this.random.bool(Math.max(0, complexity - 0.2))) {
                             clusterIntervals.push(2);
                        }
                        // Perfect 5th (Thickness) - Very High
                        if (this.random.bool(Math.max(0, complexity - 0.4))) {
                             clusterIntervals.push(7);
                        }

                        const clusterNotes = clusterIntervals.map(i => rootPC + i);

                        chord = {
                            degree: 0, // Irrelevant in atonal context
                            rootPitchClass: rootPC,
                            quality: 'dissonant',
                            type: 'cluster',
                            barIndex: currentBar + b + (s/subDivisions),
                            absoluteBeat: (currentBar + b) * 4 + (s * beatDuration),
                            duration: beatDuration,
                            section: section.type,
                            timeSig: timeSig,
                            key: 'C', // Dummy key
                            scale: 'chromatic', // THE SAFETY PIN
                            tension: 1.0, // Always max tension
                            notes: clusterNotes // Pre-populated to force voicing
                        };
                    } else {
                        // STANDARD TONAL LOGIC
                        // 1. Resolve Quality
                        const quality = getGraphChordQuality(currentDegree, sectionScale);

                        // 2. Resolve Root Pitch
                        // Degree is 0-6. Interval from root.
                        const interval = sectionIntervals[currentDegree];
                        const rootPitchClass = (sectionRootIdx + interval) % 12;

                        // Basic Type Inference
                        let type = 'triad';
                        if (quality === 'diminished') type = 'diminished';
                        if (currentDegree === 4) type = 'dom7'; // V is dominant

                        // Enhancements based on Complexity/Tension
                        const complexity = (context.config && context.config.vectors) ? context.config.vectors.harmonicComplexity : 0.5;
                        if (complexity > 0.4) {
                             if (quality === 'major') type = 'maj7';
                             if (quality === 'minor') type = 'min7';
                        }

                        chord = {
                            degree: currentDegree,
                            rootPitchClass: rootPitchClass,
                            quality: quality,
                            type: type,
                            barIndex: currentBar + b + (s/subDivisions),
                            absoluteBeat: (currentBar + b) * 4 + (s * beatDuration),
                            duration: beatDuration, // beats (assuming 4/4)
                            section: section.type,
                            timeSig: timeSig,
                            key: sectionKey,
                            scale: sectionScale,
                            tension: tension,
                            notes: [] // populated later by VoicingEngine
                        };
                    }

                    progression.push(chord);

                    // 4. Transition (Walk the Graph)
                    const edges = sectionGraph[currentDegree];
                    if (edges) {
                        // Narrative Influence: High Tension?
                        // If Tension > 0.8, maybe favor Deceptive (V -> vi)?
                        // We can tweak weights dynamically here.

                        let effectiveEdges = edges.map(e => ({...e}));

                        // Dynamic Weight Adjustment
                        if (currentDegree === 4 && tension > 0.8) {
                            // Increase weight of Deceptive Cadence (target 5)
                            effectiveEdges.forEach(e => {
                                if (e.target === 5) e.weight *= 2.0;
                            });
                        }

                        // Weighted Random Selection
                        let totalWeight = 0;
                        effectiveEdges.forEach(e => totalWeight += e.weight);

                        // VARIATION: Inject Seed Bias into Transition
                        // Use a hash of bar index + seed to force diverse paths across different generations
                        // but consistent paths within the same generation.
                        const transitionSeed = (currentBar + b + s) * 123.45;
                        const seedBias = Math.sin(transitionSeed) * 0.1; // +/- 0.1 bias

                        let r = (this.random.next() + seedBias) * totalWeight;
                        // Normalize r
                        r = Math.abs(r) % totalWeight;

                        let nextDegree = currentDegree; // Fallback

                        for (const edge of effectiveEdges) {
                            if (r < edge.weight) {
                                nextDegree = edge.target;
                                break;
                            }
                            r -= edge.weight;
                        }
                        currentDegree = nextDegree;
                    } else {
                        currentDegree = 0; // Reset to Tonic if dead end
                    }
                }
            }
            currentBar += bars;
        });

        return progression;
    }

    /**
     * Enhances a basic chord progression with functional logic:
     * - Secondary Dominants
     * - Pivot Chords
     * - Tritone Substitutions
     */
    generateTargetedProgression(template, context) {
        if (!template || template.length === 0) return [];

        const { config, traits, scaleType, rootKey } = context;
        const result = [];

        // Config Weights
        const weights = (config && config.harmony) ? config.harmony : HARMONY_WEIGHTS;

        for (let i = 0; i < template.length; i++) {
            const current = template[i];
            const next = template[i + 1];

            // INFLUENCE ENGINE: Narrative Tension
            const tension = context.getTension ? context.getTension(current.barIndex || 0) : 0.5;
            const tensionMult = 0.5 + tension; // 0.5 to 1.5 multiplier

            // --- ADAPTIVE HARMONIC RHYTHM (Split Logic) ---
            const allowSplit = (current.duration >= 3.0); // Only split full bars

            if (tension > 0.75 && allowSplit && next) {
                 const splitChords = this.applySplitBarLogic(current, next);
                 splitChords.forEach(ch => {
                     this.applyHarmonicExpansion(ch, traits ? traits.name : '', this.random, tension);
                     result.push(ch);
                 });

                 // Pivot logic applies to the last chord of the split?
                 // If modulation happens at 'next', the chord BEFORE it (the secondary dominant) is key.
                 // We skip standard pivot logic for split bars for simplicity, as V7/Next implies modulation.
                 continue;
            }

            // --- STANDARD LOGIC ---
            let processed = { ...current };

            // 1. Secondary Dominants
            // Config chance is now pre-scaled by Harmonic Freedom in SeededConfig
            const secDomChance = Math.min(0.9, (weights.secondaryDominantChance || 0.2) * tensionMult);
            if (next && this.applySecondaryDominants(processed, next, secDomChance)) {
                // Modified in place
            }

            // 2. Harmonic Expansion (Color Tones)
            this.applyHarmonicExpansion(processed, traits ? traits.name : '', this.random, tension, weights);

            // 3. Pivot Modulation
            const pivotChance = Math.min(0.9, (weights.pivotModulationChance || 0.1) * tensionMult);
            if (next && next.key && current.key && next.key !== current.key) {
                this.applyPivotModulation(processed, next, pivotChance);
            }

            result.push(processed);
        }

        // Apply Tritone Resolutions (Voice Leading Hints)
        // AND Cadence Detection (Context Awareness)
        for (let i = 0; i < result.length - 1; i++) {
            this.resolveTritone(result[i], result[i+1]);

            // Phase 1 Audit: Context Awareness
            const cadence = detectCadence(result[i], result[i+1]);
            if (cadence !== 'none') {
                result[i].cadenceType = cadence; // Mark the PREPARATION chord (V)
                result[i+1].isResolution = true; // Mark the RESOLUTION chord (I)
            }
        }

        return result;
    }

    applySplitBarLogic(current, next) {
        // Strategy: Secondary Dominant to Next
        const targetRoot = next.rootPitchClass;
        const secDomRoot = (targetRoot + 7) % 12;

        const halfDuration = current.duration / 2;
        const timeSig = current.timeSig || 4;

        const chord1 = { ...current, duration: halfDuration };
        const chord2 = {
            ...current,
            rootPitchClass: secDomRoot,
            quality: 'major',
            type: 'dom7',
            analysis: `V7/${next.degree}`,
            isSecondaryDominant: true,
            duration: halfDuration,
            absoluteBeat: (current.absoluteBeat || (current.barIndex*4)) + halfDuration,
            barIndex: (current.barIndex || 0) + (halfDuration / timeSig),
            isPassing: true
        };
        return [chord1, chord2];
    }

    applySecondaryDominants(chord, nextChord, chance) {
        if (this.random.bool(chance)) {
            // Only replace if current is not a pillar (I, IV, V)
            const isPillar = [0, 3, 4].includes(chord.degree);
            if (!isPillar) {
                chord.rootPitchClass = (nextChord.rootPitchClass + 7) % 12;
                chord.quality = 'major';
                chord.type = 'dom7';
                chord.analysis = `V7/${nextChord.degree}`;
                chord.isSecondaryDominant = true;
                return true;
            }
        }
        return false;
    }

    applyPivotModulation(chord, nextChord, chance) {
        if (this.random.bool(chance)) {
            const pivot = this.findPivotChord(chord.key, nextChord.key);
            if (pivot) {
                chord.rootPitchClass = pivot.root;
                chord.quality = pivot.quality;
                chord.type = 'triad';
                chord.analysis = 'Pivot';
                chord.isPivot = true;
                return true;
            }
        }
        return false;
    }

    applyHarmonicExpansion(chord, genreName, random, tension = 0.5, weights = {}) {
         genreName = genreName || '';

         // Use Harmonic Freedom (extensionChance) from config to drive complexity regardless of genre name
         // If weights.extensionChance is high (>0.5), force complex behavior even for Pop
         const freedom = weights.extensionChance || 0.2;

         const isComplex = genreName.includes('Jazz') || genreName.includes('Soul') || genreName.includes('Complex') || genreName.includes('Noir') || freedom > 0.3; // Lowered from 0.5 to catch medium-high freedom

         // VARIATION: Wildcard Injection (Chromatic Mediant or Borrowed)
         // Low probability event based on random seed hash, ignoring standard rules
         // This creates unique "signature" moments in a progression.
         const wildcardRoll = random.next();
         if (wildcardRoll > 0.95 && !isComplex) { // 5% chance in standard genres
             // Inject Chromatic Mediant (e.g. III major in Major key)
             if (chord.degree === 0 && chord.quality === 'major') {
                 chord.rootPitchClass = (chord.rootPitchClass + 4) % 12; // III
                 chord.degree = 2; // Approx
                 chord.quality = 'major';
                 chord.analysis = 'Chromatic Mediant';
                 return; // Skip standard expansion
             }
         }

         if (isComplex) {
              if (chord.type === 'triad') {
                  if (chord.quality === 'major') chord.type = 'maj7';
                  if (chord.quality === 'minor') chord.type = 'min7';
                  if (chord.degree === 4) chord.type = 'dom7';
              }
              // Dynamic Probability: Higher Tension -> More Extensions
              const extensionChance = 0.2 + (tension * 0.4) + (freedom * 0.3);
              if (random.bool(extensionChance)) {
                  if (chord.type.includes('7')) chord.type = chord.type.replace('7', '9');
              }
         } else if (genreName.includes('Pop') || genreName.includes('Rock')) {
              // Even in pop, high freedom allows sus4 and add9
              if (chord.degree === 4 && random.bool(0.3 + (freedom * 0.2))) {
                  chord.type = 'sus4';
              }
         }

         // Inversions (Simplified)
         // Only invert if not Root position crucial?
         // Handled by VoiceLeading mostly, but we can hint.
         // Dynamic Probability: Higher Tension -> More Inversions (Instability)
         const inversionChance = 0.05 + (tension * 0.2) + (freedom * 0.1);
         if (random.bool(inversionChance)) chord.inversion = 1;
    }

    findPivotChord(keyA, keyB) {
        // Simple Pivot: Find a diatonic chord common to both keys
        const scaleA = this.getScaleNotes(keyA);
        const scaleB = this.getScaleNotes(keyB);
        const commonRoots = scaleA.filter(n => scaleB.includes(n));

        const candidates = [];
        commonRoots.forEach(root => {
            const qA = this.getDiatonicChordQuality(root, scaleA);
            const qB = this.getDiatonicChordQuality(root, scaleB);
            if (qA && qB && qA === qB) {
                candidates.push({ root, quality: qA });
            }
        });

        if (candidates.length > 0) {
            return this.random.pick(candidates);
        }
        return null;
    }

    getDiatonicChordQuality(root, scale) {
        const rootIdx = scale.indexOf(root);
        if (rootIdx === -1) return null;

        const third = scale[(rootIdx + 2) % scale.length];
        const fifth = scale[(rootIdx + 4) % scale.length];

        const interval3 = (third - root + 12) % 12;
        const interval5 = (fifth - root + 12) % 12;

        if (interval3 === 4 && interval5 === 7) return 'major';
        if (interval3 === 3 && interval5 === 7) return 'minor';
        if (interval3 === 3 && interval5 === 6) return 'diminished';
        if (interval3 === 4 && interval5 === 8) return 'augmented';

        return null;
    }

    getScaleNotes(key) {
        const parts = key.split(' ');
        const rootStr = parts[0];
        const type = parts[1] ? parts[1].toLowerCase() : 'major';
        const rootIdx = NOTES.indexOf(rootStr);
        const intervals = SCALE_INTERVALS[type] || SCALE_INTERVALS.major;
        return intervals.map(i => (rootIdx + i) % 12);
    }

    resolveTritone(chordA, chordB) {
        if (chordA.type === 'dom7' || chordA.isSecondaryDominant) {
            const root = chordA.rootPitchClass;
            const third = (root + 4) % 12;
            const seventh = (root + 10) % 12;

            chordA.guideTones = {
                [third]: 'up',
                [seventh]: 'down'
            };
        }
    }

    /**
     * Analyzes the harmonic rhythm of a progression to determine pace and density.
     * Used by Orchestrator/Melody logic to adapt to fast/slow changes.
     */
    analyzeHarmonicRhythm(progression) {
        if (!progression || progression.length === 0) return { avgDuration: 4, pace: 'moderate' };

        // Calculate total duration in BARS (duration is usually in beats, default 4 per bar)
        // If duration is 4 beats, that's 1 bar.
        const totalBeats = progression.reduce((acc, c) => acc + (c.duration || 4), 0);
        // Assuming 4/4 time signature for calculation standardization
        const totalBars = totalBeats / 4;

        // Avg Bars per Chord
        const avgDuration = totalBars / progression.length;

        // Debug
        // console.log(`[HarmonyManager] Rhythm Analysis: Chords=${progression.length}, Beats=${totalBeats}, AvgBars=${avgDuration.toFixed(2)}`);

        let pace = 'moderate';
        if (avgDuration < 2) pace = 'fast';
        else if (avgDuration > 4) pace = 'slow';

        return {
            avgDuration: avgDuration,
            pace: pace,
            totalBars: totalBars
        };
    }
}

/**
 * Validates and resolves harmonic clashes in a generated set of events.
 * Uses a deep harmonic context map for precise validation.
 * (Preserved static function for backward compatibility)
 */
export const enforceHarmonicContext = (events, chordProgression, timeSignature = 4) => {
    if (!events || events.length === 0) return events;

    // 1. Sort events by start time for linear processing
    const sorted = [...events].sort((a, b) => a.s - b.s);

    // Optimized Chord Lookup
    const getChordAtTime = (t) => {
        const match = chordProgression.find(ch => {
            if (ch.absoluteBeat !== undefined) {
                return t >= ch.absoluteBeat && t < (ch.absoluteBeat + ch.duration);
            }
            const start = (ch.barIndex || 0) * 4;
            const end = start + (ch.duration || 4);
            return t >= start && t < end;
        });
        return match;
    };

    const validated = [];

    for (let i = 0; i < sorted.length; i++) {
        let current = sorted[i];
        const chord = getChordAtTime(current.s);

        const section = chord ? (chord.section || 'default').toUpperCase() : 'DEFAULT';
        const ts = chord ? (chord.timeSig || 4) : 4;

        let strictStrongBeat = true;
        let allowClash = false;

        if (section.includes('INTRO') || section.includes('OUTRO') || section.includes('VERSE')) {
            strictStrongBeat = true;
            allowClash = false;
        } else if (section.includes('CHORUS') || section.includes('CLIMAX') || section.includes('DROP')) {
            strictStrongBeat = false;
            allowClash = true;
        } else if (section.includes('BUILD')) {
            strictStrongBeat = true;
            allowClash = true;
        }

        // --- 1. STRONG BEAT VALIDATION (Chord Tone Lock) ---
        const barTime = current.s % ts;
        const isDownbeat = (barTime < 0.1);
        const isSecondary = (ts === 4 && Math.abs(barTime - 2.0) < 0.1) ||
                            (ts === 6 && Math.abs(barTime - 3.0) < 0.1);
        const isStrongBeat = isDownbeat || isSecondary;

        const isBass = current.role && current.role.includes('bass');
        const isMelody = current.role && current.role.includes('melody');

        const shouldLock = (isBass && isDownbeat) || (isMelody && isStrongBeat && strictStrongBeat);

        if (shouldLock && chord) {
            const chordTones = chord.notes ? chord.notes.map(n => n % 12) : [chord.rootPitchClass % 12];
            const notePC = current.d % 12;

            if (!chordTones.includes(notePC)) {
                let best = current.d;
                let minDist = Infinity;
                const currentOctave = Math.floor(current.d / 12);
                [-1, 0, 1].forEach(oct => {
                    chordTones.forEach(ct => {
                        const cand = ct + (currentOctave + oct) * 12;
                        const dist = Math.abs(cand - current.d);
                        if (dist < minDist) {
                            minDist = dist;
                            best = cand;
                        }
                    });
                });
                current.d = best;
            }
        }

        // --- 2. CLASH DETECTION ---
        // Fix: Allow dissonance for Horror/Chromatic contexts
        if (chord && (chord.scale === 'chromatic' || chord.type === 'cluster')) {
            allowClash = true;
        }

        if (!allowClash) {
            for (let j = i + 1; j < sorted.length; j++) {
                const other = sorted[j];
                if (other.s >= current.s + current.len) break;

                const startMax = Math.max(current.s, other.s);
                const endMin = Math.min(current.s + current.len, other.s + other.len);

                if (endMin > startMax) {
                    const int = Math.abs(current.d - other.d);
                    if (CLASH_INTERVALS.has(int)) {
                        const getPriority = (ev) => {
                            if (ev.role && ev.role.includes('bass')) return 10;
                            if (ev.role && ev.role.includes('melody')) return 8;
                            if (ev.role && ev.role.includes('harmony')) return 5;
                            return 1;
                        };
                        const p1 = getPriority(current);
                        const p2 = getPriority(other);
                        let victim = (p1 < p2) ? current : other;

                        // Shift victim away
                        if (victim.d < (victim === current ? other.d : current.d)) {
                            victim.d -= 1;
                        } else {
                            victim.d += 1;
                        }
                    }
                }
            }
        }
        validated.push(current);
    }

    // --- 3. STRICT SCALE CLAMP (The "Dissonance" Purge) ---
    // Final pass to ensure absolutely no out-of-scale notes (unless chromaticism is intended)
    // We reuse the chord lookups.
    return validated.map(ev => {
        if (ev.d === undefined) return ev;
        if (ev.role && (ev.role.includes('perc') || ev.role === 'drums')) return ev;

        const chord = getChordAtTime(ev.s);
        if (!chord) return ev;

        const scaleType = chord.scale || 'major';
        // Allow chromaticism for Noir/Jazz if explicitly requested, otherwise clamp
        // The Prompt requested "Strict Scale clamp".
        // Fix: Horror 'chromatic' clusters must NOT be clamped.
        if (scaleType === 'chromatic') return ev;

        const rootKey = chord.key || 'C';
        const rootIdx = NOTES.indexOf(normalizeKey(rootKey));
        const intervals = SCALE_INTERVALS[scaleType] || SCALE_INTERVALS.major;
        const scalePCs = new Set(intervals.map(i => (rootIdx + i) % 12));

        const notePC = ev.d % 12;
        if (!scalePCs.has(notePC)) {
            // Find nearest scale tone
            let minDist = Infinity;
            let bestPC = notePC;

            // Search neighbors
            intervals.forEach(i => {
                const targetPC = (rootIdx + i) % 12;
                // Calculate distance in semitones (considering wrap-around)
                let dist = Math.abs(notePC - targetPC);
                if (dist > 6) dist = 12 - dist;

                if (dist < minDist) {
                    minDist = dist;
                    bestPC = targetPC;
                }
            });

            // Apply correction
            // We need to keep the octave
            let diff = bestPC - notePC;
            // Adjust diff for shortest path
            if (diff > 6) diff -= 12;
            if (diff < -6) diff += 12;

            return { ...ev, d: ev.d + diff };
        }
        return ev;
    });
};

// Alias for backward compatibility
export const validateHarmony = enforceHarmonicContext;
